/**
 * Board #231 -- the signed-in user's notification feed.
 *
 * What this page used to be. It listed `InAppNotification` rows through the
 * `Notification` type, which describes a `Communication` -- an outbound
 * email/SMS. So four of its five columns (Recipient, Channel, Sent At,
 * Status) read fields that do not exist on the rows it was given, and every
 * one of them rendered blank or "-" forever. Its "Send Notification" button
 * POSTed to `/communications/notifications/`, which is a read-only ViewSet: a
 * hard 405 behind a modal that looked like it worked.
 *
 * What it is now: what happened, when, and whether you have read it. There is
 * no recipient column because every row on this page is addressed to the
 * reader, and no channel column because an in-app notification has no
 * channel. Send is gone -- these rows are raised by events
 * (`apps/communications/notifications.py`), and there is deliberately no way
 * for a client to post one in.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BellSlashIcon,
  CheckIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Pagination,
  Select,
} from '@/components/common';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { formatDateTime } from '@/utils';
import type { InAppNotification, InAppNotificationFilters } from '@/services/communications';

import {
  useInAppNotifications,
  useMarkAllNotificationsRead,
  useNotificationEvents,
  useSetNotificationRead,
} from './useInAppNotifications';

const ITEMS_PER_PAGE = 20;

const readOptions = [
  { value: '', label: 'Read and unread' },
  { value: 'false', label: 'Unread only' },
  { value: 'true', label: 'Read only' },
];

const priorityVariant: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  normal: 'info',
  high: 'warning',
  urgent: 'error',
};

/** An action_url is either an in-app route ('/policies/…') or, for a generated
 *  report file, an absolute URL. Only the first kind may go through the
 *  router; handing an absolute URL to <Link> produces a broken relative path. */
function isInAppRoute(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

export function NotificationsPage() {
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<InAppNotificationFilters>({});
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch();

  const { data, isLoading } = useInAppNotifications(page, filters);
  const { data: eventCatalogue } = useNotificationEvents();
  const setReadMutation = useSetNotificationRead();
  const markAllMutation = useMarkAllNotificationsRead();

  const notifications = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const unreadOnThisPage = notifications.filter((n) => !n.is_read).length;

  const eventOptions = useMemo(
    () => [{ value: '', label: 'All events' }, ...(eventCatalogue ?? [])],
    [eventCatalogue]
  );

  useEffect(() => {
    const next = debouncedTerm || undefined;
    setFilters((prev) => (prev.search === next ? prev : { ...prev, search: next }));
    setPage(1);
  }, [debouncedTerm]);

  const handleFilterChange = (key: keyof InAppNotificationFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle={
          totalItems === 0
            ? 'No notifications'
            : `${totalItems} notification${totalItems === 1 ? '' : 's'}`
        }
        breadcrumbs={[{ label: 'Communications', href: '/communications' }, { label: 'Notifications' }]}
        actions={
          <Button
            variant="outline"
            leftIcon={<CheckIcon className="w-5 h-5" />}
            onClick={() => markAllMutation.mutate()}
            isLoading={markAllMutation.isPending}
            disabled={totalItems === 0}
          >
            Mark all as read
          </Button>
        }
      />

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search notifications..."
              aria-label="Search notifications"
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<FunnelIcon className="w-5 h-5" />}
          >
            Filters
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <Select
              label="Status"
              name="notification-read-state"
              options={readOptions}
              value={filters.is_read ?? ''}
              onChange={(e) => handleFilterChange('is_read', e.target.value)}
            />
            <Select
              label="Event"
              name="notification-event"
              options={eventOptions}
              value={filters.event ?? ''}
              onChange={(e) => handleFilterChange('event', e.target.value)}
            />
            <Input
              label="From"
              name="notification-date-from"
              type="date"
              value={filters.date_from ?? ''}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
            <Input
              label="To"
              name="notification-date-to"
              type="date"
              value={filters.date_to ?? ''}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>
        )}
      </Card>

      <Card padding="none">
        {isLoading ? (
          <ListSkeleton />
        ) : notifications.length === 0 ? (
          <EmptyState
            icon={<BellSlashIcon />}
            title="No notifications"
            description="Nothing matches this search. New activity on your contacts, policies, quotes, claims and reports will appear here."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Notification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      When
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {notifications.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      notification={notification}
                      onToggleRead={() =>
                        setReadMutation.mutate({
                          id: notification.id,
                          isRead: !notification.is_read,
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {unreadOnThisPage > 0 && (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {unreadOnThisPage} unread on this page.
        </p>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  onToggleRead,
}: {
  notification: InAppNotification;
  onToggleRead: () => void;
}) {
  const label = notification.event_label || notification.event || 'Notification';

  return (
    <tr className={notification.is_read ? 'bg-white dark:bg-gray-800' : 'bg-primary-50/40 dark:bg-primary-900/20'}>
      <td className="px-6 py-4 align-top whitespace-nowrap">
        <Badge variant={priorityVariant[notification.priority] ?? 'default'} size="sm">
          {label}
        </Badge>
      </td>
      <td className="px-6 py-4">
        <p className={notification.is_read ? 'text-sm text-gray-900 dark:text-gray-100' : 'text-sm font-semibold text-gray-900 dark:text-gray-100'}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{notification.message}</p>
        {notification.action_url && isInAppRoute(notification.action_url) && (
          <Link
            to={notification.action_url}
            className="mt-1 inline-block text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-400"
          >
            {notification.action_text || 'Open'}
          </Link>
        )}
        {notification.action_url && !isInAppRoute(notification.action_url) && (
          <a
            href={notification.action_url}
            className="mt-1 inline-block text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-400"
            rel="noreferrer"
          >
            {notification.action_text || 'Open'}
          </a>
        )}
      </td>
      <td className="px-6 py-4 align-top text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {formatDateTime(notification.created_at)}
      </td>
      <td className="px-6 py-4 align-top text-right">
        <div className="flex items-center justify-end gap-3">
          <Badge variant={notification.is_read ? 'default' : 'info'} size="sm">
            {notification.is_read ? 'Read' : 'Unread'}
          </Badge>
          <button
            type="button"
            onClick={onToggleRead}
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-400"
          >
            {notification.is_read ? 'Mark as unread' : 'Mark as read'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function ListSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 dark:bg-gray-700" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 border-t border-gray-100 dark:border-gray-700 flex items-center px-6 gap-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28" />
          <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16" />
        </div>
      ))}
    </div>
  );
}
